import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Save, X, Plus, Search, Filter, CheckCircle, XCircle } from 'lucide-react';
import { 
  getAllSuppliers, 
  updateSupplier, 
  deleteSupplier, 
  createSupplier,
  activateSupplier,
  fetchSuppliersByName,
  fetchSuppliersByStatus
} from '../../../../services/apiSupplier';

const SupplierManagement = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    contactPerson: '',
    email: '',
    phone: '',
    address: '',
    taxId: '',
    paymentTerms: '30days',
    isActive: true
  });

  const paymentTermsOptions = [
    { value: 'immediate', label: 'Immediate' },
    { value: '15days', label: '15 Days' },
    { value: '30days', label: '30 Days' },
    { value: '45days', label: '45 Days' },
    { value: '60days', label: '60 Days' }
  ];

  // Fetch suppliers on component mount
  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Fetch suppliers with optional filters
  const fetchSuppliers = async () => {
    try {
      setLoading(true);
      setError('');
      
      let data;
      if (searchTerm) {
        data = await fetchSuppliersByName(searchTerm);
      } else if (statusFilter !== 'all') {
        const isActive = statusFilter === 'active';
        data = await fetchSuppliersByStatus(isActive);
      } else {
        data = await getAllSuppliers();
      }
      
      setSuppliers(data.data || data || []);
    } catch (err) {
      setError(err.toString());
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle search
  const handleSearch = async () => {
    await fetchSuppliers();
  };

  // Handle filter change
  const handleFilterChange = async (filter) => {
    setStatusFilter(filter);
    setSearchTerm('');
    await fetchSuppliers();
  };

  // Start editing a supplier
  const startEdit = (supplier) => {
    setEditingId(supplier._id);
    setEditForm({ ...supplier });
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  // Save edited supplier
  const saveEdit = async () => {
    try {
      setError('');
      const updatedSupplier = await updateSupplier(editingId, editForm);
      
      setSuppliers(suppliers.map(supplier => 
        supplier._id === editingId ? updatedSupplier.supplier || updatedSupplier : supplier
      ));
      
      setEditingId(null);
      setEditForm({});
    } catch (err) {
      setError(err.toString());
      console.error('Error updating supplier:', err);
    }
  };

  // Delete supplier
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        setError('');
        await deleteSupplier(id);
        setSuppliers(suppliers.filter(supplier => supplier._id !== id));
      } catch (err) {
        setError(err.toString());
        console.error('Error deleting supplier:', err);
      }
    }
  };

  // Toggle supplier active status
  const toggleActiveStatus = async (supplier) => {
    try {
      setError('');
      if (!supplier.isActive) {
        await activateSupplier(supplier._id);
      } else {
        // For deactivation, we'll use the update function
        await updateSupplier(supplier._id, { ...supplier, isActive: false });
      }
      
      setSuppliers(suppliers.map(s => 
        s._id === supplier._id ? { ...s, isActive: !s.isActive } : s
      ));
    } catch (err) {
      setError(err.toString());
      console.error('Error toggling supplier status:', err);
    }
  };

  // Add new supplier
  const handleAddSupplier = async () => {
    try {
      setError('');
      const createdSupplier = await createSupplier(newSupplier);
      setSuppliers([createdSupplier.supplier || createdSupplier, ...suppliers]);
      setNewSupplier({
        name: '',
        contactPerson: '',
        email: '',
        phone: '',
        address: '',
        taxId: '',
        paymentTerms: '30days',
        isActive: true
      });
      setShowAddForm(false);
    } catch (err) {
      setError(err.toString());
      console.error('Error creating supplier:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">Supplier Management</h1>
        
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {/* Search and Filter Controls */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="flex-1 min-w-64">
            <div className="relative">
              <input
                type="text"
                placeholder="Search suppliers by name..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>
          </div>
          
          <button
            onClick={handleSearch}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Search
          </button>

          <select
            value={statusFilter}
            onChange={(e) => handleFilterChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Suppliers</option>
            <option value="active">Active Only</option>
            <option value="inactive">Inactive Only</option>
          </select>

          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Plus className="h-4 w-4" />
            Add Supplier
          </button>
        </div>

        {/* Add Supplier Form */}
        {showAddForm && (
          <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold mb-4">Add New Supplier</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <input
                type="text"
                placeholder="Supplier Name"
                className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                value={newSupplier.name}
                onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
              />
              <input
                type="text"
                placeholder="Contact Person"
                className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                value={newSupplier.contactPerson}
                onChange={(e) => setNewSupplier({...newSupplier, contactPerson: e.target.value})}
              />
              <input
                type="email"
                placeholder="Email"
                className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                value={newSupplier.email}
                onChange={(e) => setNewSupplier({...newSupplier, email: e.target.value})}
              />
              <input
                type="tel"
                placeholder="Phone"
                className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                value={newSupplier.phone}
                onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})}
              />
              <input
                type="text"
                placeholder="Address"
                className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                value={newSupplier.address}
                onChange={(e) => setNewSupplier({...newSupplier, address: e.target.value})}
              />
              <input
                type="text"
                placeholder="Tax ID"
                className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                value={newSupplier.taxId}
                onChange={(e) => setNewSupplier({...newSupplier, taxId: e.target.value})}
              />
              <select
                className="px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                value={newSupplier.paymentTerms}
                onChange={(e) => setNewSupplier({...newSupplier, paymentTerms: e.target.value})}
              >
                {paymentTermsOptions.map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={handleAddSupplier}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
              >
                Add Supplier
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Suppliers Table */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Supplier Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Payment Terms
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {suppliers.map((supplier) => (
                <tr key={supplier._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === supplier._id ? (
                      <div className="space-y-2">
                        <input
                          type="text"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          value={editForm.name || ''}
                          onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                        />
                        <input
                          type="text"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          value={editForm.contactPerson || ''}
                          onChange={(e) => setEditForm({...editForm, contactPerson: e.target.value})}
                        />
                        <input
                          type="text"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          value={editForm.taxId || ''}
                          onChange={(e) => setEditForm({...editForm, taxId: e.target.value})}
                          placeholder="Tax ID"
                        />
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                        <div className="text-sm text-gray-500">Contact: {supplier.contactPerson}</div>
                        {supplier.taxId && <div className="text-sm text-gray-500">Tax ID: {supplier.taxId}</div>}
                      </div>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === supplier._id ? (
                      <div className="space-y-2">
                        <input
                          type="email"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          value={editForm.email || ''}
                          onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                        />
                        <input
                          type="tel"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          value={editForm.phone || ''}
                          onChange={(e) => setEditForm({...editForm, phone: e.target.value})}
                        />
                        <input
                          type="text"
                          className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          value={editForm.address || ''}
                          onChange={(e) => setEditForm({...editForm, address: e.target.value})}
                          placeholder="Address"
                        />
                      </div>
                    ) : (
                      <div>
                        <div className="text-sm text-gray-900">{supplier.email}</div>
                        <div className="text-sm text-gray-500">{supplier.phone}</div>
                        {supplier.address && <div className="text-sm text-gray-500">{supplier.address}</div>}
                      </div>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    {editingId === supplier._id ? (
                      <select
                        className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                        value={editForm.paymentTerms || ''}
                        onChange={(e) => setEditForm({...editForm, paymentTerms: e.target.value})}
                      >
                        {paymentTermsOptions.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    ) : (
                      <span className="text-sm text-gray-900">
                        {paymentTermsOptions.find(opt => opt.value === supplier.paymentTerms)?.label || supplier.paymentTerms}
                      </span>
                    )}
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => toggleActiveStatus(supplier)}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                        supplier.isActive 
                          ? 'bg-green-100 text-green-800 hover:bg-green-200' 
                          : 'bg-red-100 text-red-800 hover:bg-red-200'
                      }`}
                    >
                      {supplier.isActive ? (
                        <>
                          <CheckCircle className="h-3 w-3" />
                          Active
                        </>
                      ) : (
                        <>
                          <XCircle className="h-3 w-3" />
                          Inactive
                        </>
                      )}
                    </button>
                  </td>
                  
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    {editingId === supplier._id ? (
                      <div className="flex space-x-2">
                        <button
                          onClick={saveEdit}
                          className="text-green-600 hover:text-green-900 p-1"
                        >
                          <Save className="h-4 w-4" />
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-gray-600 hover:text-gray-900 p-1"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex space-x-2">
                        <button
                          onClick={() => startEdit(supplier)}
                          className="text-blue-600 hover:text-blue-900 p-1"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(supplier._id)}
                          className="text-red-600 hover:text-red-900 p-1"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {suppliers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">No suppliers found</div>
            <div className="text-gray-400 text-sm mt-2">
              {searchTerm ? 'Try adjusting your search criteria' : 'Add your first supplier to get started'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierManagement;